import { useCallback, useState } from 'react';
import type { GistPhotoDecision, GistPhotoQueueItem } from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { BlobPicture } from '@london-life/ui/components/blob-picture';
import { Button } from '@london-life/ui/components/button';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useAdmin } from '../lib/admin-context';
import { when } from '../lib/format';
import { useRequest } from '../lib/use-request';

const STATUS: Record<GistPhotoQueueItem['status'], string> = {
  checking: 'Waiting',
  approved: 'Approved',
  rejected: 'Rejected',
};

// a picture the API serves to the team (a Gist photo, whatever its state)
export function TeamPicture({ src, alt }: { src: string; alt: string }) {
  return <BlobPicture id={src} load={() => api.picture(src)} alt={alt} className="admin-gist-photo" />;
}

// Gist photos from the in-game camera: only their poster sees one until the team approves it here (the oldest
// waiting first); a rejected photo comes off its post and the poster is told. A recent two-step check, audited.
export function GistPhotos({ onDone }: { onDone: (message: string) => void }) {
  const { can } = useAdmin();
  const [all, setAll] = useState(false);
  const photos = useRequest(useCallback(() => api.admin.gistPhotos(all), [all]));
  const [deciding, setDeciding] = useState<{
    photo: GistPhotoQueueItem;
    outcome: GistPhotoDecision['outcome'];
  } | null>(null);
  const [note, setNote] = useState('');
  const decide = async () => {
    if (!deciding) return;
    await api.admin.decideGistPhoto(deciding.photo.id, {
      outcome: deciding.outcome,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    onDone(deciding.outcome === 'approve' ? 'Photo approved: everyone sees it now.' : 'Photo rejected.');
    await photos.reload();
  };
  const ask = (photo: GistPhotoQueueItem, outcome: GistPhotoDecision['outcome']) => {
    setNote('');
    setDeciding({ photo, outcome });
  };
  const list = photos.data?.photos ?? [];
  return (
    <>
      <h2 className="content-subtitle">Photos</h2>
      <div className="admin-toolbar">
        <Button type="button" variant={all ? 'outline' : 'default'} onClick={() => setAll(false)}>
          Waiting{photos.data ? ` (${photos.data.open})` : ''}
        </Button>
        <Button type="button" variant={all ? 'default' : 'outline'} onClick={() => setAll(true)}>
          All photos
        </Button>
      </div>
      {photos.error && <ErrorNotice error={photos.error} onRetry={() => void photos.reload()} />}
      {photos.busy && !photos.data && <p className="admin-muted">Loading…</p>}
      {photos.data && list.length === 0 && (
        <div className="admin-empty">
          <strong className="admin-empty-title">{all ? 'No photos yet.' : 'No photos waiting.'}</strong>
        </div>
      )}
      <ul className="admin-gist-photos" aria-label="Photos">
        {list.map((photo) => (
          <li key={photo.id} className="admin-panel" data-testid="gist-photo">
            <TeamPicture src={photo.src} alt={`Photo by ${photo.author.name}`} />
            <div className="admin-player-head">
              <h2>
                📷 <span className="admin-muted">by {photo.author.name}</span>
              </h2>
              <Badge variant={photo.status === 'checking' ? 'default' : 'secondary'}>{STATUS[photo.status]}</Badge>
            </div>
            {photo.postBody && <p>{photo.postBody}</p>}
            <small className="admin-muted">
              {when(photo.at)}
              {photo.reviewedAt ? ` · decided ${when(photo.reviewedAt)}` : ''}
              {photo.note ? ` · ${photo.note}` : ''}
            </small>
            {photo.status === 'checking' && can('players.support') && (
              <div className="admin-page-actions wrap">
                <Button type="button" onClick={() => ask(photo, 'approve')}>
                  Approve
                </Button>
                <Button type="button" variant="destructive" onClick={() => ask(photo, 'reject')}>
                  Reject
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <FormDialog
        open={!!deciding}
        title={deciding?.outcome === 'approve' ? 'Approve this photo' : 'Reject this photo'}
        description={
          deciding?.outcome === 'approve'
            ? 'Everyone who can see the post sees the photo.'
            : 'The photo comes off its post (the post stays) and its poster is told.'
        }
        submitLabel={deciding?.outcome === 'approve' ? 'Approve' : 'Reject'}
        onSubmit={decide}
        onClose={() => setDeciding(null)}
      >
        <div className="admin-field">
          <Label htmlFor="gist-photo-note">Note for the audit log (optional)</Label>
          <Textarea
            id="gist-photo-note"
            maxLength={300}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </FormDialog>
    </>
  );
}
